import React, { useState, useEffect } from 'react';
import {
  HelpCircle,
  Search,
  MessageSquare,
  BookOpen,
  LifeBuoy,
  PlusCircle,
  ThumbsUp,
  ChevronDown,
  ChevronUp,
  Tag,
  Building,
  Filter,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Send,
  Eye,
  ShieldAlert,
} from 'lucide-react';
import { helpService } from './helpService';
import { HelpCategory, HelpFaq, SupportTicket, HelpCenterOverviewKpi } from './types';
import { CreateTicketModal } from './components/CreateTicketModal';
import { CreateFaqModal } from './components/CreateFaqModal';
import { TicketDetailModal } from './components/TicketDetailModal';

export const HelpPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'faq' | 'tickets'>('faq');
  const [loading, setLoading] = useState(true);
  const [kpi, setKpi] = useState<HelpCenterOverviewKpi | null>(null);

  // FAQ States
  const [categories, setCategories] = useState<HelpCategory[]>([]);
  const [faqs, setFaqs] = useState<HelpFaq[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [expandedFaqId, setExpandedFaqId] = useState<string | null>(null);

  // Tickets States
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [ticketStatusFilter, setTicketStatusFilter] = useState('');
  const [adminOrgFilter, setAdminOrgFilter] = useState('');
  const [organizations, setOrganizations] = useState<Array<{ id: string; organization_code: string; legal_name: string }>>([]);

  // Modals
  const [isCreateTicketOpen, setIsCreateTicketOpen] = useState(false);
  const [isCreateFaqOpen, setIsCreateFaqOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);

  // Current session user info
  const currentUser = (() => {
    try {
      const u = localStorage.getItem('auth_user');
      return u ? JSON.parse(u) : null;
    } catch {
      return null;
    }
  })();

  const isSystemAdmin = currentUser?.user_type === 'SYSTEM_ADMIN';
  const userDisplayName = currentUser?.display_name || 'User';
  const userEmail = currentUser?.primary_email || '';
  const orgName = currentUser?.organization?.name || 'Organization';
  const orgCode = currentUser?.organization?.code || '';
  const orgId = currentUser?.organization?.id || '';

  useEffect(() => {
    loadData();
  }, [selectedCategory, ticketStatusFilter, adminOrgFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [overviewData, catList, faqList, ticketList] = await Promise.all([
        helpService.getOverview(),
        helpService.getCategories(),
        helpService.getFaqs({
          category_id: selectedCategory || undefined,
          search: searchQuery || undefined,
        }),
        helpService.getTickets({
          status: ticketStatusFilter || undefined,
          filter_org_id: adminOrgFilter || undefined,
        }),
      ]);

      setKpi(overviewData);
      setCategories(catList);
      setFaqs(faqList);
      setTickets(ticketList);

      if (isSystemAdmin && organizations.length === 0) {
        const orgList = await helpService.getOrganizations();
        setOrganizations(orgList);
      }
    } catch (err) {
      console.error('Failed to load help data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const faqList = await helpService.getFaqs({
        category_id: selectedCategory || undefined,
        search: searchQuery || undefined,
      });
      setFaqs(faqList);
    } catch (err) {
      console.error('Search failed:', err);
    }
  };

  const handleMarkHelpful = async (faqId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await helpService.markHelpful(faqId);
      setFaqs((prev) =>
        prev.map((f) => (f.id === faqId ? { ...f, helpful_count: f.helpful_count + 1 } : f))
      );
    } catch (err) {
      console.error(err);
    }
  };

  const openTicketDetail = async (ticket: SupportTicket) => {
    try {
      const full = await helpService.getTicketById(ticket.id);
      setSelectedTicket(full);
    } catch (err: any) {
      alert('Failed to load ticket details: ' + err.message);
    }
  };

  return (
    <div style={{ maxWidth: '1440px', margin: '0 auto', padding: '0 24px 48px 24px' }}>
      {/* Top Banner */}
      <div className="page-header-banner">
        <div className="badge">LUMINIX HELP & SUPPORT CENTER</div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: '16px',
          }}
        >
          <div>
            <h1>Help & Support Center</h1>
            <p>
              Knowledge base, FAQs, SOPs, and technical support ticketing for {orgName} ({orgCode}).
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            {isSystemAdmin && (
              <button
                onClick={() => setIsCreateFaqOpen(true)}
                className="btn-secondary"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: 'auto',
                  padding: '10px 18px',
                  background: '#ffffff',
                }}
              >
                <PlusCircle size={16} />
                <span>+ Add FAQ / Guide</span>
              </button>
            )}
            <button
              onClick={() => setIsCreateTicketOpen(true)}
              className="btn-primary"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: 'auto',
                padding: '10px 18px',
              }}
            >
              <Send size={16} />
              <span>+ Open New Support Ticket</span>
            </button>
          </div>
        </div>
      </div>

      {/* KPI Cards Bar */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '16px',
          marginBottom: '24px',
        }}
      >
        <div className="stat-card" style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '18px' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
            Total Guide Articles
          </div>
          <div style={{ fontSize: '28px', fontWeight: 800, color: '#0f172a', margin: '4px 0' }}>
            {kpi?.totalFaqs || faqs.length}
          </div>
          <div style={{ fontSize: '12px', color: '#0284c7' }}>
            {kpi?.globalFaqs || 0} Global • {kpi?.orgFaqs || 0} Org Specific
          </div>
        </div>

        <div className="stat-card" style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '18px' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
            {isSystemAdmin ? 'Assigned Open Tickets' : 'My Open Tickets'}
          </div>
          <div style={{ fontSize: '28px', fontWeight: 800, color: '#eab308', margin: '4px 0' }}>
            {kpi?.myOpenTickets || 0}
          </div>
          <div style={{ fontSize: '12px', color: '#64748b' }}>
            Awaiting support response
          </div>
        </div>

        <div className="stat-card" style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '18px' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
            My Resolved Tickets
          </div>
          <div style={{ fontSize: '28px', fontWeight: 800, color: '#10b981', margin: '4px 0' }}>
            {kpi?.myResolvedTickets || 0}
          </div>
          <div style={{ fontSize: '12px', color: '#10b981' }}>
            Successfully resolved
          </div>
        </div>

        {isSystemAdmin && (
          <div className="stat-card" style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '18px' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#1e40af', textTransform: 'uppercase' }}>
              🛡️ Support Team Queue (All Orgs)
            </div>
            <div style={{ fontSize: '28px', fontWeight: 800, color: '#1d4ed8', margin: '4px 0' }}>
              {kpi?.allOpenTickets || 0} Open
            </div>
            <div style={{ fontSize: '12px', color: '#1e40af' }}>
              {kpi?.allInProgressTickets || 0} In Progress • {kpi?.allResolvedTickets || 0} Resolved
            </div>
          </div>
        )}
      </div>

      {/* Main Tabs Navigation */}
      <div className="nav-tabs" style={{ marginBottom: '24px' }}>
        <button
          onClick={() => setActiveTab('faq')}
          className={`tab-btn ${activeTab === 'faq' ? 'active' : ''}`}
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <BookOpen size={16} />
          <span>FAQ & Knowledge Base</span>
          <span className="badge-pill badge-neutral" style={{ fontSize: '11px', padding: '2px 8px' }}>
            {faqs.length}
          </span>
        </button>

        <button
          onClick={() => setActiveTab('tickets')}
          className={`tab-btn ${activeTab === 'tickets' ? 'active' : ''}`}
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <MessageSquare size={16} />
          <span>
            {isSystemAdmin ? 'Support Tickets (All Organizations)' : 'My Support Tickets'}
          </span>
          <span className="badge-pill badge-neutral" style={{ fontSize: '11px', padding: '2px 8px' }}>
            {tickets.length}
          </span>
        </button>
      </div>

      {/* TAB 1: FAQ & KNOWLEDGE BASE */}
      {activeTab === 'faq' && (
        <div>
          {/* Search and Category Filters */}
          <div
            style={{
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '16px 20px',
              marginBottom: '24px',
              display: 'flex',
              gap: '16px',
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <form onSubmit={handleSearchSubmit} style={{ flex: 1, minWidth: '280px', display: 'flex', gap: '8px' }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <Search
                  size={18}
                  style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}
                />
                <input
                  type="text"
                  placeholder="Search questions, keywords or procedures..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    paddingLeft: '38px',
                    paddingRight: '12px',
                    paddingTop: '9px',
                    paddingBottom: '9px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '13px',
                  }}
                />
              </div>
              <button type="submit" className="btn-primary" style={{ width: 'auto', padding: '8px 16px', fontSize: '13px' }}>
                Search
              </button>
            </form>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Filter size={16} color="#64748b" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px' }}
              >
                <option value="">All Categories</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* FAQ Accordion List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {faqs.length === 0 ? (
              <div
                style={{
                  background: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '48px 24px',
                  textAlign: 'center',
                }}
              >
                <LifeBuoy size={40} color="#94a3b8" style={{ margin: '0 auto 12px auto' }} />
                <h3 style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b', marginBottom: '6px' }}>
                  No questions found matching your search criteria
                </h3>
                <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                  Try a different search term or submit a new ticket to our technical support team.
                </p>
                <button onClick={() => setIsCreateTicketOpen(true)} className="btn-primary" style={{ width: 'auto' }}>
                  Create Support Ticket
                </button>
              </div>
            ) : (
              faqs.map((faq) => {
                const isExpanded = expandedFaqId === faq.id;
                return (
                  <div
                    key={faq.id}
                    style={{
                      background: '#ffffff',
                      border: isExpanded ? '1px solid #93c5fd' : '1px solid #e2e8f0',
                      borderRadius: '10px',
                      overflow: 'hidden',
                      transition: 'all 0.2s ease',
                      boxShadow: isExpanded ? '0 4px 6px -1px rgba(0,0,0,0.05)' : 'none',
                    }}
                  >
                    <div
                      onClick={() => setExpandedFaqId(isExpanded ? null : faq.id)}
                      style={{
                        padding: '16px 20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        background: isExpanded ? '#f8fafc' : '#ffffff',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: '18px' }}>
                          {faq.is_global ? '🌐' : '🏢'}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <span
                              className={`badge-pill ${faq.is_global ? 'badge-info' : 'badge-warning'}`}
                              style={{ fontSize: '10px', padding: '2px 6px' }}
                            >
                              {faq.is_global ? 'Global System Guide' : `${orgCode} Specific`}
                            </span>
                            {faq.category_title && (
                              <span style={{ fontSize: '11px', color: '#64748b' }}>
                                • {faq.category_title}
                              </span>
                            )}
                          </div>
                          <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#0f172a' }}>
                            {faq.question}
                          </h4>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginLeft: '12px' }}>
                        <span style={{ fontSize: '12px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Eye size={14} />
                          {faq.view_count}
                        </span>
                        {isExpanded ? <ChevronUp size={20} color="#64748b" /> : <ChevronDown size={20} color="#64748b" />}
                      </div>
                    </div>

                    {isExpanded && (
                      <div style={{ padding: '20px', borderTop: '1px solid #f1f5f9', background: '#ffffff' }}>
                        <div
                          style={{
                            fontSize: '14px',
                            color: '#334155',
                            lineHeight: 1.6,
                            whiteSpace: 'pre-wrap',
                            marginBottom: '16px',
                          }}
                        >
                          {faq.answer}
                        </div>

                        {faq.tags && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' }}>
                            <Tag size={13} color="#94a3b8" />
                            {faq.tags.split(',').map((t, idx) => (
                              <span
                                key={idx}
                                style={{
                                  background: '#f1f5f9',
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  color: '#475569',
                                }}
                              >
                                #{t.trim()}
                              </span>
                            ))}
                          </div>
                        )}

                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            paddingTop: '12px',
                            borderTop: '1px solid #f8fafc',
                            fontSize: '12px',
                            color: '#64748b',
                          }}
                        >
                          <span>Published by: <strong>{faq.created_by_name || 'System Admin'}</strong></span>
                          <button
                            onClick={(e) => handleMarkHelpful(faq.id, e)}
                            className="btn-secondary"
                            style={{
                              width: 'auto',
                              padding: '6px 12px',
                              fontSize: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                            }}
                          >
                            <ThumbsUp size={14} />
                            <span>Helpful ({faq.helpful_count})</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* TAB 2: SUPPORT TICKETS */}
      {activeTab === 'tickets' && (
        <div>
          {/* Tickets Filter Bar */}
          <div
            style={{
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '16px 20px',
              marginBottom: '20px',
              display: 'flex',
              gap: '16px',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>
                Filter:
              </span>
              <select
                value={ticketStatusFilter}
                onChange={(e) => setTicketStatusFilter(e.target.value)}
                style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
              >
                <option value="">All Statuses</option>
                <option value="OPEN">Open (OPEN)</option>
                <option value="IN_PROGRESS">In Progress (IN_PROGRESS)</option>
                <option value="WAITING_ON_USER">Waiting on User</option>
                <option value="RESOLVED">Resolved (RESOLVED)</option>
                <option value="CLOSED">Closed (CLOSED)</option>
              </select>

              {isSystemAdmin && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Building size={16} color="#64748b" />
                  <select
                    value={adminOrgFilter}
                    onChange={(e) => setAdminOrgFilter(e.target.value)}
                    style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                  >
                    <option value="">All Organizations</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.legal_name} ({org.organization_code})
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <button
              onClick={() => setIsCreateTicketOpen(true)}
              className="btn-primary"
              style={{ width: 'auto', padding: '8px 16px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <Send size={15} />
              <span>Create Ticket</span>
            </button>
          </div>

          {/* Tickets Table / List */}
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Ticket #</th>
                  <th>Subject & Description</th>
                  {isSystemAdmin && <th>Organization</th>}
                  <th>Category</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Date & Messages</th>
                  <th style={{ textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {tickets.length === 0 ? (
                  <tr>
                    <td colSpan={isSystemAdmin ? 8 : 7} style={{ textAlign: 'center', padding: '36px', color: '#64748b' }}>
                      No support tickets found.
                    </td>
                  </tr>
                ) : (
                  tickets.map((t) => (
                    <tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => openTicketDetail(t)}>
                      <td style={{ fontFamily: 'monospace', fontWeight: 700, color: '#2563eb' }}>
                        {t.ticket_number}
                      </td>
                      <td>
                        <div style={{ fontWeight: 600, color: '#0f172a' }}>{t.subject}</div>
                        <div style={{ fontSize: '12px', color: '#64748b', maxWidth: '360px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {t.description}
                        </div>
                      </td>
                      {isSystemAdmin && (
                        <td>
                          <span className="badge-pill badge-neutral" style={{ fontSize: '11px' }}>
                            🏢 {t.organization_name || t.organization_code || t.organization_id}
                          </span>
                        </td>
                      )}
                      <td>
                        <span style={{ fontSize: '12px', color: '#475569', fontWeight: 500 }}>
                          {t.category}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`badge-pill ${
                            t.priority === 'URGENT' || t.priority === 'HIGH'
                              ? 'badge-danger'
                              : t.priority === 'MEDIUM'
                              ? 'badge-warning'
                              : 'badge-neutral'
                          }`}
                        >
                          {t.priority}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`badge-pill ${
                            t.status === 'RESOLVED'
                              ? 'badge-success'
                              : t.status === 'IN_PROGRESS'
                              ? 'badge-info'
                              : t.status === 'OPEN'
                              ? 'badge-warning'
                              : 'badge-neutral'
                          }`}
                        >
                          {t.status}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>
                          {new Date(t.created_at).toLocaleDateString()}
                        </div>
                        <div style={{ fontSize: '11px', color: '#2563eb', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <MessageSquare size={12} />
                          {t.messages_count || 1} msg
                        </div>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openTicketDetail(t);
                          }}
                          className="btn-secondary"
                          style={{ width: 'auto', padding: '4px 12px', fontSize: '12px' }}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal: Create Ticket */}
      <CreateTicketModal
        isOpen={isCreateTicketOpen}
        onClose={() => setIsCreateTicketOpen(false)}
        onCreated={(newTicket) => {
          setTickets((prev) => [newTicket, ...prev]);
          if (kpi) setKpi({ ...kpi, myOpenTickets: kpi.myOpenTickets + 1 });
        }}
        userDisplayName={userDisplayName}
        userEmail={userEmail}
      />

      {/* Modal: Create FAQ (System Admin only) */}
      <CreateFaqModal
        isOpen={isCreateFaqOpen}
        onClose={() => setIsCreateFaqOpen(false)}
        categories={categories}
        organizations={organizations}
        currentOrgId={orgId}
        onCreated={(newFaq) => {
          setFaqs((prev) => [newFaq, ...prev]);
        }}
        userDisplayName={userDisplayName}
      />

      {/* Modal: Ticket Detail & Conversation */}
      {selectedTicket && (
        <TicketDetailModal
          isOpen={true}
          onClose={() => setSelectedTicket(null)}
          ticket={selectedTicket}
          isSystemAdmin={isSystemAdmin}
          userDisplayName={userDisplayName}
          onUpdated={(updated) => {
            setSelectedTicket(updated);
            setTickets((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
          }}
        />
      )}
    </div>
  );
};

export default HelpPage;
